import { agentLiterals } from '../model/literals'
import { useCallback, useEffect, useRef, useState } from 'react'
import { agentRequest } from '../api/client'
import { IAgentSchedule } from '../model/scheduling'
export type ISchedulePayload = Pick<IAgentSchedule, 'cron' | 'message' | 'description' | 'is_one_time'>
export interface IScheduleRow {
  schedule: IAgentSchedule
  pending?: boolean
  error?: string
}

interface IScheduleSession {
  agentId: string | null
  active: boolean
  read: number
  pending: Set<string>
}

export function useAgentSchedules(agentId: string | null) {
  const [rows, setRows] = useState<IScheduleRow[]>([])
  const [loading, setLoading] = useState(!!agentId)
  const [error, setError] = useState('')
  const session = useRef<IScheduleSession>()
  const nextId = useRef(0)
  const load = useCallback(async () => {
    const currentSession = session.current
    if (!agentId || !currentSession?.active || currentSession.agentId !== agentId || currentSession.pending.size) return
    const read = ++currentSession.read
    setLoading(true)
    try {
      const response = await agentRequest(`/agents/${agentId}/schedules`)
      const data = await response.json()
      if (!Array.isArray(data)) throw new Error('Invalid schedules')
      if (currentSession.active && currentSession.read === read) {
        setRows(data.map((schedule) => ({ schedule })))
        setError('')
      }
    } catch {
      if (currentSession.active && currentSession.read === read) setError(agentLiterals.ag_err_load_schedules.fallback)
    } finally {
      if (currentSession.active && currentSession.read === read) setLoading(false)
    }
  }, [agentId])
  useEffect(() => {
    const currentSession: IScheduleSession = { agentId, active: true, read: 0, pending: new Set() }
    session.current = currentSession
    setRows([])
    setError('')
    setLoading(!!agentId)
    void load()
    return () => {
      currentSession.active = false
    }
  }, [agentId, load])

  const save = async (payload: ISchedulePayload, existing?: IAgentSchedule): Promise<IAgentSchedule | undefined> => {
    const currentSession = session.current
    if (
      !agentId ||
      !currentSession?.active ||
      currentSession.agentId !== agentId ||
      (existing && (existing.agent_id !== agentId || currentSession.pending.has(existing.id)))
    )
      return
    const id = existing?.id || `pending-${Date.now()}-${nextId.current++}`
    const optimistic: IAgentSchedule = {
      id,
      agent_id: agentId,
      job_id: null,
      tz_name: null,
      created_at: new Date().toISOString(),
      ...existing,
      ...payload,
    }
    currentSession.pending.add(id)
    // A read started before this mutation must not replace its optimistic or confirmed result.
    currentSession.read += 1
    setLoading(false)
    setRows((current) =>
      existing
        ? current.map((row) => (row.schedule.id === id ? { schedule: optimistic, pending: true } : row))
        : [...current, { schedule: optimistic, pending: true }]
    )
    try {
      const response = await agentRequest(`/agents/${agentId}/schedules${existing ? `/${id}` : ''}`, {
        method: existing ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      })
      const text = await response.text()
      const result = text ? JSON.parse(text) : existing ? optimistic : undefined
      if (!result || typeof result.id !== 'string') throw new Error('Missing schedule confirmation')
      if (currentSession.active) {
        const confirmed: IAgentSchedule = { ...optimistic, ...result }
        setRows((current) => current.map((row) => (row.schedule.id === id ? { schedule: confirmed } : row)))
        return confirmed
      }
    } catch {
      if (currentSession.active)
        setRows((current) =>
          current.map((row) =>
            row.schedule.id === id
              ? {
                  schedule: existing || optimistic,
                  error: agentLiterals.ag_err_save_confirm.fallback,
                }
              : row
          )
        )
    } finally {
      currentSession.pending.delete(id)
    }
  }
  const remove = async (schedule: IAgentSchedule): Promise<boolean> => {
    const currentSession = session.current
    if (
      !agentId ||
      !currentSession?.active ||
      currentSession.agentId !== agentId ||
      schedule.agent_id !== agentId ||
      currentSession.pending.has(schedule.id)
    )
      return false
    currentSession.pending.add(schedule.id)
    currentSession.read += 1
    setLoading(false)
    setRows((current) => current.map((row) => (row.schedule.id === schedule.id ? { ...row, pending: true } : row)))
    try {
      await agentRequest(`/agents/${agentId}/schedules/${schedule.id}`, { method: 'DELETE' })
      if (currentSession.active) {
        setRows((current) => current.filter((row) => row.schedule.id !== schedule.id))
        return true
      }
    } catch {
      if (currentSession.active)
        setRows((current) =>
          current.map((row) =>
            row.schedule.id === schedule.id
              ? { ...row, pending: false, error: agentLiterals.ag_err_del_confirm.fallback }
              : row
          )
        )
    } finally {
      currentSession.pending.delete(schedule.id)
    }
    return false
  }
  const currentAgent = session.current?.active && session.current.agentId === agentId
  return {
    rows: currentAgent ? rows : [],
    loading: !!agentId && (!currentAgent || loading),
    error: currentAgent ? error : '',
    load,
    save,
    remove,
  }
}
